/**
 * Sending the follow-up link again — the same one, or a new one.
 *
 * A dialog rather than a button because the two are genuinely different acts
 * and the difference is invisible from outside: both put an email in somebody's
 * inbox. Only one of them silently kills the link that person is already
 * holding, along with whatever they had half-filled in against it. That is the
 * kind of consequence this product states before it happens, not after.
 *
 * It is also where the engine's rule becomes visible: reminders re-send the
 * SAME link, and only a person, deliberately, ever mints a new one.
 */
import { useState } from 'react';
import { Modal } from '../../components/fs';
import type { FollowUpSummary } from '../../api/forms';
import { resendInviteApi } from '../../api/forms';

export default function FollowUpResendDialog({ submissionId, submissionRef, name, followUp, initial, onClose, onSent }: {
  submissionId: number;
  submissionRef: string;
  /** an answer that names the submission, for the subtitle */
  name?: string;
  followUp: FollowUpSummary;
  /**
   * Which option to open on. "Send it to them after all" is the same act as a
   * same-link re-send, so it lands here with `same` chosen and no argument.
   */
  initial?: 'same' | 'fresh';
  onClose: () => void;
  onSent: () => void;
}) {
  const live = !!followUp.invite && !followUp.invite.revoked
    && new Date(followUp.invite.expiresAt).getTime() > Date.now();
  const [mode, setMode] = useState<'same' | 'fresh'>(initial || (live ? 'same' : 'fresh'));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const to = followUp.invite?.email || 'whoever the link is addressed to';
  const saved = followUp.saved;

  const send = async () => {
    setSending(true); setError('');
    try {
      await resendInviteApi(submissionId, mode);
      onSent();
    } catch (e) {
      setError((e as Error).message);
      setSending(false);
    }
  };

  return (
    <Modal
      title="Re-send the follow-up link"
      onClose={onClose}
      footer={
        <>
          <button className="fs-btn primary" disabled={sending} onClick={send}>
            {sending ? 'Sending…' : 'Send it'}
          </button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      }
    >
      <div className="fs-stack">
        <div className="fs-page-sub" style={{ marginTop: -4 }}>
          {submissionRef}{name ? ` · ${name}` : ''} · to <b>{to}</b>
        </div>
        {error && <div className="ad-err">{error}</div>}

        <div>
          <label className={`fs-choice${mode === 'same' ? ' on' : ''}`}>
            <input type="radio" name="fu-resend" checked={mode === 'same'} disabled={!live}
              onChange={() => setMode('same')} />
            <span>
              <b>Re-send the same link</b>
              <small>
                {live
                  ? 'What reminders do. Anything they already saved stays with it.'
                  : 'Not available — this link has expired or has already been used.'}
              </small>
            </span>
          </label>
          <label className={`fs-choice${mode === 'fresh' ? ' on' : ''}`}>
            <input type="radio" name="fu-resend" checked={mode === 'fresh'}
              onChange={() => setMode('fresh')} />
            <span>
              <b>Mint a fresh link</b>
              <small>For a link that expired, or went to the wrong person.</small>
            </span>
          </label>
        </div>

        {mode === 'fresh' ? (
          <div className="fs-consequence warn">
            A fresh link is emailed to <b>{to}</b> and <b>the old link stops working immediately</b>
            {saved
              ? <> — including {saved.count !== null && saved.label
                ? `the ${saved.count} ${saved.label}`
                : 'the work'} already saved against it, which cannot be recovered</>
              : null}
            . This is logged on the submission with your name.
          </div>
        ) : (
          <div className="fs-consequence">
            The link they already have goes out again, unchanged — as a reply on the ticket the first
            send opened, where the step is a helpdesk. Nothing they have saved is affected, and the
            reminder schedule carries on from where it is.
          </div>
        )}
      </div>
    </Modal>
  );
}
